/** The generic file-upload/serve endpoint and its accept rules. */

/**
 * The **generic file-upload/serve** REST endpoint. A local-disk-backed store — no S3/CDN — with uploads living at
 * `<world>/uploads/<category>/<id>.<ext>`. `category` is caller-chosen (e.g.
 * `pilot-portraits`) so this stays a reusable service, not hardcoded to one consumer.
 *
 *   POST /api/uploads/<category>/<id>   → save; `multipart/form-data` body, field `file`.
 *                                          Server derives `<ext>` from the file's declared
 *                                          content type (never trusts a client-supplied
 *                                          extension) — one of png/jpg/jpeg/webp, 5 MB cap.
 *                                          Re-uploading the same `category`/`id` overwrites.
 *                                          → 201 {@link UploadResponse} / 400 bad request /
 *                                          413 too large / 415 unsupported type.
 *   GET  /api/uploads/<category>/<id>.<ext> → serve the stored bytes with the matching
 *                                          `Content-Type` → 200 binary / 404 not found.
 *
 * `category` and `id` are restricted to a safe, hand-typeable charset (letters, digits,
 * dash, underscore — no path separators or traversal); see `isSafeUploadSegment` in the
 * backend's `uploads.ts`.
 */
export const UPLOADS_PATH = "/api/uploads" as const;

/** The maximum accepted upload size in bytes (5 MB) — shared by the server's reject and any client-side pre-check. */
export const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;

/**
 * The image content types the upload service accepts — for a client-side pre-check and the
 * file-picker `accept` attribute (nicer UX). The **server** remains the real gate: it derives
 * the stored extension from the upload's declared type (`extensionForContentType`, backend
 * `uploads.ts`) and rejects anything not in this set with `415`. Keep the two in sync.
 */
export const ALLOWED_UPLOAD_CONTENT_TYPES = ["image/png", "image/jpeg", "image/webp"] as const;

/** The POST {@link UPLOADS_PATH} success response — the server-resolved storage path (relative to `<world>/uploads/`). */
export type UploadResponse = {
  ok: true;
  category: string;
  id: string;
  ext: string;
  /** `<category>/<id>.<ext>`, relative to `<world>/uploads/` — also the GET route's suffix. */
  path: string;
}

/** The upload category pilot portraits are filed under (`<world>/uploads/pilot-portraits/<id>.<ext>`). */
export const PILOT_PORTRAITS_CATEGORY = "pilot-portraits";

/** The upload category mech portraits are filed under (`<world>/uploads/mech-portraits/<id>.<ext>`). */
export const MECH_PORTRAITS_CATEGORY = "mech-portraits";

/**
 * The upload category a GM-set board token image is filed under
 * (`<world>/uploads/token-images/<id>.<ext>`), `projects/token-art.md` Phase 3. The browser's
 * unit inspector and `lvtt encounter token-image --file` both upload here, then write the returned
 * URL onto the combatant with `set_token_image`.
 */
export const TOKEN_IMAGES_CATEGORY = "token-images";

/**
 * The upload id for one token-image upload: `<encounterId>--<combatantId>--v<version>`, with every
 * character outside the upload service's safe charset replaced by `_`. Keyed on the encounter too
 * because combatant ids are only unique within one encounter.
 *
 * **Every upload gets its own file** — `version` must differ per upload (callers pass
 * {@link newTokenImageVersion}). The id is NOT stable per unit, unlike the portrait uploads,
 * because `set_token_image` is undoable: undo restores the previous URL, and that URL must still
 * resolve to the previous bytes on every client. With a stable id a re-upload overwrote the file
 * in place, so after undo the GM's cache showed the old image while a fresh client fetched the
 * new bytes. Superseded files stay on disk (small, GM-only, and an undo can still point at them).
 * The id is capped at 100 characters by trimming the unit part, never the version.
 */
export function tokenImageUploadId(encounterId: string, combatantId: string, version: string): string {
  const safe = (s: string): string => s.replace(/[^A-Za-z0-9_-]/g, "_");
  const suffix = `--v${safe(version)}`.slice(0, 40);
  const unit = `${safe(encounterId)}--${safe(combatantId)}`;
  return `${(/^[A-Za-z0-9]/.test(unit) ? unit : `u${unit}`).slice(0, 100 - suffix.length)}${suffix}`;
}

/** A fresh per-upload version token for {@link tokenImageUploadId}: the upload time plus a random tail, both base 36, so two uploads in the same millisecond still differ. */
export function newTokenImageVersion(): string {
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
}
